import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import type { Session } from '@supabase/supabase-js'
import { hasSupabaseConfig } from '../../../lib/config/env'
import { getSupabaseBrowserClient } from '../../../lib/supabase/client'
import { clearOnboardingMode } from '../../onboarding/lib/entry'
import { clearOnboardingProgress } from '../../onboarding/lib/progress'

type AuthMode = 'supabase'

type AuthUser = {
  id: string
  email: string | null
  fullName?: string | null
}

type AuthContextValue = {
  isReady: boolean
  isAuthenticated: boolean
  mode: AuthMode
  session: Session | null
  user: AuthUser | null
  signInWithPassword: (email: string, password: string) => Promise<{ error?: string }>
  signUpWithPassword: (email: string, password: string, fullName?: string) => Promise<{ error?: string; needsEmailConfirmation?: boolean }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: Readonly<PropsWithChildren>) {
  const [isReady, setIsReady] = useState(false)
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<AuthUser | null>(null)

  useEffect(() => {
    const supabase = getSupabaseBrowserClient()

    if (supabase && hasSupabaseConfig) {
      supabase.auth.getSession().then(({ data }) => {
        setSession(data.session)
        setUser(
          data.session?.user
            ? {
                id: data.session.user.id,
                email: data.session.user.email ?? null,
                fullName: data.session.user.user_metadata.full_name ?? null,
              }
            : null,
        )
        setIsReady(true)
      })

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, nextSession) => {
        setSession(nextSession)
        setUser(
          nextSession?.user
            ? {
                id: nextSession.user.id,
                email: nextSession.user.email ?? null,
                fullName: nextSession.user.user_metadata.full_name ?? null,
              }
            : null,
        )
        setIsReady(true)
      })

      return () => subscription.unsubscribe()
    }
    setIsReady(true)

    return undefined
  }, [])

  useEffect(() => {
    if (!isReady) {
      return
    }

    clearOnboardingProgress()
    clearOnboardingMode()
  }, [isReady, user?.id])

  const value = useMemo<AuthContextValue>(() => {
    return {
      isReady,
      isAuthenticated: Boolean(user),
      mode: 'supabase',
      session,
      user,
      async signInWithPassword(email, password) {
        const supabase = getSupabaseBrowserClient()
        if (!supabase) {
          return { error: 'Die Konfiguration ist unvollständig. Anmeldung ist derzeit nicht verfügbar.' }
        }

        const { error } = await supabase.auth.signInWithPassword({ email, password })
        return error ? { error: error.message } : {}
      },
      async signUpWithPassword(email, password, fullName) {
        const supabase = getSupabaseBrowserClient()
        if (!supabase) {
          return { error: 'Die Konfiguration ist unvollständig. Registrierung ist derzeit nicht verfügbar.' }
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName ?? null,
            },
          },
        })

        if (error) {
          return { error: error.message }
        }

        const needsEmailConfirmation = !data.session
        return { needsEmailConfirmation }
      },
      async signOut() {
        const supabase = getSupabaseBrowserClient()
        if (supabase) {
          await supabase.auth.signOut()
        }
        setSession(null)
        setUser(null)
      },
    }
  }, [isReady, session, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }

  return context
}